/**
 * Bundle Builder Component
 * Handles product selection, Skio API integration, and cart operations
 */

class BundleBuilder {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.boxProductId = container.dataset.boxProductId;
    this.maxSelections = null; // Will be set from API response or box_sizes setting

    // Box size selector configuration
    this.availableSizes = container.dataset.boxSizes
      ? container.dataset.boxSizes.split(',').map(s => parseInt(s.trim(), 10)).filter(n => n > 0)
      : [];

    // Free gift configuration
    this.giftEnabled = container.dataset.giftEnabled === 'true';
    this.giftVariantIds = container.dataset.giftVariantIds
      ? container.dataset.giftVariantIds.split(',').map(id => id.trim()).filter(id => id)
      : [];
    this.giftSubscriptionOnly = container.dataset.giftSubscriptionOnly === 'true';
    this.giftDisplayData = container.dataset.giftDisplayData
      ? JSON.parse(container.dataset.giftDisplayData)
      : [];

    // State - now using a Map to track quantities per variant
    this.selectedProducts = new Map(); // variantId -> { product, variant, quantity }
    this.products = [];
    this.productMetafields = new Map(); // productHandle -> metafields
    this.boxData = null;
    this.isSubscription = false; // Default to one-time purchase
    this.selectedSellingPlan = null;

    // Upsells state
    this.oneTimeUpsells = [];
    this.selectedUpsells = new Map(); // variantId -> { product, variant, quantity }
    this.currentStep = 'selection'; // 'selection' | 'upsells'
    this.upsellSettings = {}; // otuHeader, otuSubheader, etc.

    // DOM Elements
    this.grid = container.querySelector('#bundle-products-grid');
    this.countElements = container.querySelectorAll('.bundle-builder__count');
    this.maxElements = container.querySelectorAll('.bundle-builder__max');
    this.drawer = container.querySelector('#bundle-drawer');
    this.drawerBody = container.querySelector('.bundle-builder__drawer-body');

    // Floating bar elements
    this.floatingBar = container.querySelector('.bundle-builder__floating-bar');
    this.floatingSlotsContainer = container.querySelector('.bundle-builder__selected-items');
    this.floatingSlots = []; // Will be populated after API response
    this.floatingUpsellsContainer = container.querySelector('.bundle-builder__selected-upsells');
    this.floatingGiftsContainer = container.querySelector('.bundle-builder__selected-gifts');
    this.floatingSelectionStatus = container.querySelector('.bundle-builder__selection-status');
    this.floatingAddButton = container.querySelector('.bundle-builder__floating-add');
    this.floatingAddCompare = container.querySelector('.bundle-builder__floating-add-compare');
    this.floatingAddCurrent = container.querySelector('.bundle-builder__floating-add-current');

    // Info area slots (in product info sidebar)
    this.infoSlotsContainer = container.querySelector('.bundle-builder__info-slots-container');
    this.infoSlots = []; // Will be populated after API response

    // Buy button block (in product info area)
    this.buyButtonBlock = container.querySelector('.bundle-builder__buy-button');

    // Upsells step elements
    this.selectionStep = container.querySelector('.bundle-builder__selection-step');
    this.upsellsStep = container.querySelector('.bundle-builder__upsells-step');
    this.upsellsGrid = container.querySelector('.bundle-builder__upsells-grid');
    this.upsellsHeader = container.querySelector('.bundle-builder__upsells-header');
    this.backToBoxLink = container.querySelector('.bundle-builder__back-to-box');

    // Legacy elements (keep for backwards compatibility)
    this.slots = this.floatingSlots;
    this.addToCartButton = this.floatingAddButton;

    // Price elements - Skio will update these via external_price_selector
    this.priceCurrentElements = container.querySelectorAll('.bundle-builder__price-current');

    // Main price display element (in blocks area)
    this.mainPriceDisplay = container.querySelector('.bundle-builder__price-display [skio-external-price]');

    // Plan picker element and configuration
    this.planPicker = container.querySelector('.bundle-builder__plan-picker');
    this.planPickerConfig = this.planPicker ? {
      layout: this.planPicker.dataset.layout || 'vertical',
      startOnetime: this.planPicker.dataset.startOnetime !== 'false',
      onetimeFirst: this.planPicker.dataset.onetimeFirst !== 'false',
      showRadio: this.planPicker.dataset.showRadio !== 'false',
      onetimeTitle: this.planPicker.dataset.onetimeTitle || 'One time purchase',
      subscriptionTitle: this.planPicker.dataset.subscriptionTitle || 'Subscribe',
      discountFormat: this.planPicker.dataset.discountFormat || 'percent',
      discountStyle: this.planPicker.dataset.discountStyle || 'bubble',
      discountText: this.planPicker.dataset.discountText || 'Save [discount]',
      selectorType: this.planPicker.dataset.selectorType || 'dropdown',
      showComparePrice: this.planPicker.dataset.showComparePrice !== 'false',
      // Subscription discount override: when > 0, applies this % from original price instead of Skio's cascading discount
      subscriptionDiscountOverride: parseFloat(this.planPicker.dataset.subscriptionDiscountOverride) || 0,
      additionalSubscriptionContent: this.planPicker.dataset.additionalSubscriptionContent || ''
    } : null;

    this.init();
  }

  async init() {
    this.bindEvents();
    await this.fetchBoxData();
    this.renderProducts();
    this.updateUI();
  }

  bindEvents() {
    // Listen for Skio selling plan updates (when Skio plan picker is available)
    document.addEventListener('skio::update-selling-plan', (e) => {
      if (e.detail) {
        this.selectedSellingPlan = e.detail.sellingPlanId;
        this.isSubscription = !!e.detail.sellingPlanId;
        this.updatePricing();
        this.updateUI(); // Update button text based on subscription state
      }
    });

    // Floating bar slot remove buttons
    this.floatingSlots.forEach(slot => {
      const removeBtn = slot.querySelector('.bundle-builder__slot-remove');
      if (removeBtn) {
        removeBtn.addEventListener('click', () => {
          const slotIndex = parseInt(slot.dataset.slot, 10) - 1;
          // Get flattened selection to find which product is in this slot
          const flatSelections = [];
          this.selectedProducts.forEach(item => {
            for (let i = 0; i < item.quantity; i++) {
              flatSelections.push(item);
            }
          });
          if (flatSelections[slotIndex]) {
            this.decreaseQuantity(flatSelections[slotIndex].variantId);
          }
        });
      }
    });

    // Floating bar add to cart button
    if (this.floatingAddButton) {
      this.floatingAddButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.handlePrimaryButtonClick();
      });
    }

    // Buy button block (in product info area)
    if (this.buyButtonBlock) {
      this.buyButtonBlock.addEventListener('click', (e) => {
        e.preventDefault();
        this.handlePrimaryButtonClick();
      });
    }

    // Back to box link (on upsells step)
    if (this.backToBoxLink) {
      this.backToBoxLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.showSelectionStep();
      });
    }

    // Add to cart with upsells button (on upsells step)
    const addWithUpsellsButton = this.container.querySelector('.bundle-builder__add-with-upsells');
    if (addWithUpsellsButton) {
      addWithUpsellsButton.addEventListener('click', async (e) => {
        e.preventDefault();
        await this.addToCart(); // Add bundle + upsells together
      });
    }

    // Skip upsells button - add bundle to cart (without upsells) and go to cart
    const skipButton = this.container.querySelector('.bundle-builder__skip-upsells');
    if (skipButton) {
      skipButton.addEventListener('click', async (e) => {
        e.preventDefault();
        // Add bundle to cart without upsells, then open cart
        await this.addBundleToCart();
      });
    }

    // Drawer close
    const drawerClose = this.container.querySelector('.bundle-builder__drawer-close');
    const drawerOverlay = this.container.querySelector('.bundle-builder__drawer-overlay');
    if (drawerClose) {
      drawerClose.addEventListener('click', () => this.closeDrawer());
    }
    if (drawerOverlay) {
      drawerOverlay.addEventListener('click', () => this.closeDrawer());
    }

    // Close drawer on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeDrawer();
    });
  }

  async fetchBoxData() {
    try {
      const response = await fetch('https://api.skio.com/storefront-http/get-dynamic-box', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productPlatformId: this.boxProductId
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch box data: ${response.status}`);
      }

      const responseData = await response.json();

      // Handle nested DynamicBox structure
      const data = responseData.DynamicBox || responseData;

      this.boxData = data;

      // Determine max selections: theme setting overrides API
      if (this.availableSizes.length > 0) {
        this.maxSelections = this.availableSizes[0];
      } else if (data.sizeInterval && data.sizeInterval.length > 0) {
        this.maxSelections = data.sizeInterval[0];
      } else if (data.sizeRange && data.sizeRange.length > 0) {
        this.maxSelections = data.sizeRange[0];
      } else {
        this.maxSelections = 2;
      }

      // Update max display elements
      this.maxElements.forEach(el => {
        el.textContent = this.maxSelections;
      });

      // Render size selector if multiple sizes available
      if (this.availableSizes.length > 1) {
        this.renderSizeSelector();
      }

      // Generate floating bar slots based on maxSelections
      this.generateFloatingSlots();

      // Extract products from selectableProductVariants
      if (data.selectableProductVariants && data.selectableProductVariants.length > 0) {
        this.products = data.selectableProductVariants;
      }

      // Set up selling plans (subscription options) and render plan picker
      if (data.sellingPlanGroup && Object.keys(data.sellingPlanGroup).length > 0) {
        this.setupSellingPlans(data.sellingPlanGroup);
        this.renderPlanPicker();
      }

      // Update title from Skio (overrides the default theme setting)
      // Custom theme setting takes priority if changed from default
      // Use querySelectorAll to update both desktop and mobile titles
      const themeHeading = this.container.dataset.customHeading?.trim();
      const skioTitle = data.settings?.header;
      const defaultHeading = 'Build Your Own Bundle';

      // If theme heading is blank or matches default, use Skio title; otherwise keep custom theme heading
      const useSkioTitle = !themeHeading || themeHeading === defaultHeading;
      const displayTitle = useSkioTitle && skioTitle ? skioTitle : (themeHeading || defaultHeading);

      if (displayTitle) {
        const titleElements = this.container.querySelectorAll('.product__title');
        titleElements.forEach(el => {
          el.textContent = displayTitle;
        });
      }

      // Set initial pricing
      this.updatePricing();

      // Extract one-time upsells if available
      if (data.oneTimeUpsells && data.oneTimeUpsells.length > 0) {
        this.oneTimeUpsells = data.oneTimeUpsells;
        // Store upsell settings
        this.upsellSettings = {
          header: data.settings?.otuHeader || 'Choose Your Add-on',
          subheader: data.settings?.otuSubheader || '',
          productOrder: data.settings?.otuProductOrder || [],
          variantOrder: data.settings?.otuProductVariantOrder || []
        };
        // Sort upsells by configured order if specified
        if (this.upsellSettings.productOrder.length > 0) {
          this.oneTimeUpsells.sort((a, b) => {
            const aId = a.productPlatformId?.split('/').pop() || '';
            const bId = b.productPlatformId?.split('/').pop() || '';
            const aIndex = this.upsellSettings.productOrder.indexOf(aId);
            const bIndex = this.upsellSettings.productOrder.indexOf(bId);
            return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
          });
        }
      }

    } catch (error) {
      console.error('Error fetching Skio box data:', error);
      this.showError('Unable to load products. Please refresh the page.');
    }
  }

  generateFloatingSlots() {
    if (!this.floatingSlotsContainer) return;

    // Clear existing slots
    this.floatingSlotsContainer.innerHTML = '';

    // Generate slots based on maxSelections
    for (let i = 1; i <= this.maxSelections; i++) {
      const slot = document.createElement('div');
      slot.className = 'bundle-builder__selected-slot';
      slot.dataset.slot = i;
      slot.innerHTML = `
        <div class="bundle-builder__slot-empty">
          <span>${i}</span>
        </div>
        <div class="bundle-builder__slot-filled" style="display: none;">
          <img src="" alt="" class="bundle-builder__slot-image">
          <button type="button" class="bundle-builder__slot-remove" aria-label="Remove item">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      `;

      // Bind remove button event
      const removeBtn = slot.querySelector('.bundle-builder__slot-remove');
      if (removeBtn) {
        removeBtn.addEventListener('click', () => {
          const slotIndex = parseInt(slot.dataset.slot, 10) - 1;
          const flatSelections = [];
          this.selectedProducts.forEach(item => {
            for (let j = 0; j < item.quantity; j++) {
              flatSelections.push(item);
            }
          });
          if (flatSelections[slotIndex]) {
            this.decreaseQuantity(flatSelections[slotIndex].variantId);
          }
        });
      }

      this.floatingSlotsContainer.appendChild(slot);
    }

    // Update the floatingSlots reference
    this.floatingSlots = this.floatingSlotsContainer.querySelectorAll('.bundle-builder__selected-slot');

    // Also generate info slots
    this.generateInfoSlots();
  }

  generateInfoSlots() {
    if (!this.infoSlotsContainer) return;

    // Clear existing slots
    this.infoSlotsContainer.innerHTML = '';

    // Generate slots based on maxSelections
    for (let i = 1; i <= this.maxSelections; i++) {
      const slot = document.createElement('div');
      slot.className = 'bundle-builder__info-slot';
      slot.dataset.slot = i;
      slot.innerHTML = `
        <div class="bundle-builder__slot-empty">
          <span>${i}</span>
        </div>
        <div class="bundle-builder__slot-filled" style="display: none;">
          <img src="" alt="" class="bundle-builder__slot-image">
          <button type="button" class="bundle-builder__slot-remove" aria-label="Remove item">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      `;

      // Bind remove button event
      const removeBtn = slot.querySelector('.bundle-builder__slot-remove');
      if (removeBtn) {
        removeBtn.addEventListener('click', () => {
          const slotIndex = parseInt(slot.dataset.slot, 10) - 1;
          const flatSelections = [];
          this.selectedProducts.forEach(item => {
            for (let j = 0; j < item.quantity; j++) {
              flatSelections.push(item);
            }
          });
          if (flatSelections[slotIndex]) {
            this.decreaseQuantity(flatSelections[slotIndex].variantId);
          }
        });
      }

      this.infoSlotsContainer.appendChild(slot);
    }

    // Update the infoSlots reference
    this.infoSlots = this.infoSlotsContainer.querySelectorAll('.bundle-builder__info-slot');
  }

  getTierDiscount(tierObj) {
    if (!tierObj) return null;
    const sizeKey = this.maxSelections.toString();
    if (tierObj[sizeKey] != null) return tierObj[sizeKey];
    const values = Object.values(tierObj);
    return values.length > 0 ? values[0] : null;
  }

  renderSizeSelector() {
    const selectorEl = this.container.querySelector('.bundle-builder__size-selector');
    if (!selectorEl) return;

    selectorEl.innerHTML = '';

    this.availableSizes.forEach(size => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-builder__size-btn';
      if (size === this.maxSelections) btn.classList.add('is-active');
      btn.dataset.size = size;

      const label = document.createElement('span');
      label.className = 'bundle-builder__size-label';
      label.textContent = `${size} Bags`;
      btn.appendChild(label);

      const discount = this.getSizeDiscount(size);
      if (discount) {
        const badge = document.createElement('span');
        badge.className = 'bundle-builder__size-discount';
        badge.textContent = discount;
        if (this.isSubscription) badge.hidden = true;
        btn.appendChild(badge);
      }

      if (size >= 4) {
        const shipBadge = document.createElement('span');
        shipBadge.className = 'bundle-builder__size-free-shipping';
        shipBadge.textContent = 'Free shipping';
        btn.appendChild(shipBadge);
      }

      btn.addEventListener('click', () => this.changeBoxSize(size));
      selectorEl.appendChild(btn);
    });

    selectorEl.style.display = '';
  }

  getSizeDiscount(size) {
    if (!this.boxData) return null;
    const sizeKey = size.toString();
    if (this.boxData.percentDiscountTier && this.boxData.percentDiscountTier[sizeKey] != null) {
      const val = parseFloat(this.boxData.percentDiscountTier[sizeKey]);
      return val > 0 ? `${val}% off` : null;
    }
    if (this.boxData.fixedDiscountTier && this.boxData.fixedDiscountTier[sizeKey] != null) {
      const amount = parseFloat(this.boxData.fixedDiscountTier[sizeKey]);
      return amount > 0 ? `$${(amount / 100).toFixed(0)} off` : null;
    }
    return null;
  }

  getNextTierIncentive(currentCount) {
    if (!this.boxData || !this.availableSizes.length) return null;
    const tierData = this.boxData.percentDiscountTier || this.boxData.fixedDiscountTier;
    if (!tierData) return null;
    const isPercent = !!this.boxData.percentDiscountTier;

    // Find the next size above the current count that has a non-zero discount
    const nextSize = this.availableSizes.find(size => size > currentCount && tierData[size.toString()] != null && parseFloat(tierData[size.toString()]) > 0);
    if (!nextSize) return null;

    const remaining = nextSize - currentCount;
    const discount = tierData[nextSize.toString()];
    const discountText = isPercent
      ? `<span class="bundle-builder__tier-incentive-discount">${discount}% off</span>`
      : `<span class="bundle-builder__tier-incentive-discount">$${(parseFloat(discount) / 100).toFixed(0)} off</span>`;

    return `Add ${remaining} more to get ${discountText}!`;
  }

  updateTierIncentive(totalQuantity) {
    const incentiveEl = this.container.querySelector('.bundle-builder__tier-incentive');
    if (!incentiveEl) return;

    // Hide tier incentive for subscriptions (discounts don't stack) or upsells step
    if (this.isSubscription || this.currentStep === 'upsells' || totalQuantity < this.maxSelections) {
      incentiveEl.style.display = 'none';
      return;
    }

    const message = this.getNextTierIncentive(this.maxSelections);
    if (message) {
      incentiveEl.innerHTML = message;
      incentiveEl.style.display = '';
    } else {
      incentiveEl.style.display = 'none';
    }
  }

  changeBoxSize(newSize) {
    if (newSize === this.maxSelections) return;

    this.maxSelections = newSize;

    // Update max display elements
    this.maxElements.forEach(el => {
      el.textContent = this.maxSelections;
    });

    // Trim selections if they exceed new max
    const totalQuantity = this.getTotalQuantity();
    if (totalQuantity > this.maxSelections) {
      let excess = totalQuantity - this.maxSelections;
      // Remove from last-added items
      const entries = Array.from(this.selectedProducts.entries());
      for (let i = entries.length - 1; i >= 0 && excess > 0; i--) {
        const [variantId, item] = entries[i];
        const removeQty = Math.min(item.quantity, excess);
        if (removeQty >= item.quantity) {
          this.selectedProducts.delete(variantId);
        } else {
          item.quantity -= removeQty;
        }
        excess -= removeQty;
      }
    }

    // Regenerate slots and update UI
    this.generateFloatingSlots();
    this.updateUI();
    this.updatePricing();
    this.updatePlanPickerPrices();

    // Update active state on size buttons
    const buttons = this.container.querySelectorAll('.bundle-builder__size-btn');
    buttons.forEach(btn => {
      btn.classList.toggle('is-active', parseInt(btn.dataset.size, 10) === newSize);
    });
  }

  setupSellingPlans(sellingPlanGroup) {
    if (!this.frequencySelect || !sellingPlanGroup) return;

    this.frequencySelect.innerHTML = '';

    Object.entries(sellingPlanGroup).forEach(([planId, plan], index) => {
      const option = document.createElement('option');
      option.value = planId;

      // Format interval text
      let intervalText = '';
      const count = plan.intervalCount || 1;
      const interval = plan.interval || 'MONTH';

      if (count === 1) {
        intervalText = interval === 'WEEK' ? 'Every week' :
                       interval === 'MONTH' ? 'Every month' :
                       `Every ${interval.toLowerCase()}`;
      } else {
        const plural = interval === 'WEEK' ? 'weeks' :
                       interval === 'MONTH' ? 'months' :
                       `${interval.toLowerCase()}s`;
        intervalText = `Every ${count} ${plural}`;
      }

      option.textContent = plan.title || intervalText;
      this.frequencySelect.appendChild(option);

      // Set first as default
      if (index === 0) {
        this.selectedSellingPlan = planId;
      }
    });
  }

  async renderPlanPicker() {
    if (!this.planPicker || !this.boxData?.sellingPlanGroup) {
      return;
    }

    const sellingPlanGroup = this.boxData.sellingPlanGroup;
    const hasSubscriptions = Object.keys(sellingPlanGroup).length > 0;

    // If no subscriptions available, hide the picker
    if (!hasSubscriptions) {
      this.planPicker.style.display = 'none';
      return;
    }

    // Wait for the custom element to be defined (module script loads async)
    await customElements.whenDefined('bundle-plan-picker');

    // Create the bundle-plan-picker LitElement component
    this.planPickerElement = document.createElement('bundle-plan-picker');
    // Override *:empty { display: none } - shadow DOM content doesn't count for :empty
    this.planPickerElement.style.display = 'block';

    this.planPickerElement.key = this.sectionId;
    this.planPickerElement.sellingPlanGroup = sellingPlanGroup;
    this.planPickerElement.setAttribute('layout', this.planPickerConfig?.layout || 'vertical');

    // Set configuration options
    this.planPickerElement.options = {
      layout: this.planPickerConfig?.layout || 'vertical',
      start_onetime: this.planPickerConfig?.startOnetime !== false,
      onetime_first: this.planPickerConfig?.onetimeFirst !== false,
      show_radio_selector: this.planPickerConfig?.showRadio !== false,
      onetime_title: this.planPickerConfig?.onetimeTitle || 'One time purchase',
      subscription_title: this.planPickerConfig?.subscriptionTitle || 'Subscribe',
      discount_format: this.planPickerConfig?.discountFormat || 'percent',
      discount_style: this.planPickerConfig?.discountStyle || 'bubble',
      selector_type: this.planPickerConfig?.selectorType || 'dropdown',
      // Subscription discount override: when > 0, applies this % from original price instead of Skio's cascading discount
      subscription_discount_override: this.planPickerConfig?.subscriptionDiscountOverride || 0,
      additional_subscription_content: this.planPickerConfig?.additionalSubscriptionContent || '',
    };

    // Calculate initial prices
    this.updatePlanPickerPrices();

    // Clear and append the component
    this.planPicker.innerHTML = '';
    this.planPicker.appendChild(this.planPickerElement);
  }

  updatePlanPickerPrices() {
    // Calculate base total from selected products
    let originalTotal = 0;
    this.selectedProducts.forEach(item => {
      originalTotal += item.price * item.quantity;
    });

    // Apply bundle discount (same % for all box sizes)
    let bundleDiscountedTotal = originalTotal;
    if (this.boxData?.percentDiscountTier) {
      const tierDiscount = this.getTierDiscount(this.boxData.percentDiscountTier);
      if (tierDiscount) {
        bundleDiscountedTotal = originalTotal * (1 - parseFloat(tierDiscount) / 100);
      }
    } else if (this.boxData?.fixedDiscountTier) {
      const tierDiscount = this.getTierDiscount(this.boxData.fixedDiscountTier);
      if (tierDiscount) {
        bundleDiscountedTotal = originalTotal - parseFloat(tierDiscount);
      }
    }

    // Store for use in updatePricing()
    this.bundleDiscountedTotal = bundleDiscountedTotal;

    // Update the component if it exists
    if (this.planPickerElement) {
      this.planPickerElement.bundleTotal = originalTotal;
      this.planPickerElement.bundleDiscountedTotal = bundleDiscountedTotal;
      this.planPickerElement.bundleComplete = this.getTotalQuantity() === this.maxSelections;
    }
  }

  async renderProducts() {
    if (!this.grid) return;

    // Clear loading state
    this.grid.innerHTML = '';

    if (this.products.length === 0) {
      this.grid.innerHTML = '<p class="bundle-builder__empty">No products available.</p>';
      return;
    }

    // Fetch product metafields for all products
    await this.fetchProductMetafields();

    this.products.forEach((product, index) => {
      // Get the first variant that matches 8oz/1/2 lb, or fall back to first variant
      let variant = product.productVariants?.[0];

      // Try to find an 8oz or 1/2 lb variant
      if (product.productVariants && product.productVariants.length > 1) {
        const smallVariant = product.productVariants.find(v =>
          v.productVariantTitle?.toLowerCase().includes('1/2 lb') ||
          v.productVariantTitle?.toLowerCase().includes('8oz') ||
          v.productVariantTitle?.toLowerCase().includes('8 oz')
        );
        if (smallVariant) {
          variant = smallVariant;
        }
      }

      if (!variant) return;

      const card = document.createElement('div');
      card.className = 'bundle-product-card';
      card.dataset.productId = product.productPlatformId;
      card.dataset.variantId = variant.platformId;
      card.dataset.productHandle = product.productHandle;

      // Calculate price with discount
      const originalPrice = variant.price;
      let discountedPrice = originalPrice;
      let discountPercent = 0;

      // Check for tier discount (same % for all box sizes)
      if (this.boxData?.percentDiscountTier) {
        const tierDiscount = this.getTierDiscount(this.boxData.percentDiscountTier);
        if (tierDiscount) {
          discountPercent = parseFloat(tierDiscount);
          discountedPrice = originalPrice * (1 - discountPercent / 100);
        }
      } else if (this.boxData?.fixedDiscountTier) {
        const tierDiscount = this.getTierDiscount(this.boxData.fixedDiscountTier);
        if (tierDiscount) {
          const perItemDiscount = parseFloat(tierDiscount) / this.maxSelections;
          discountedPrice = originalPrice - perItemDiscount;
        }
      }

      const hasDiscount = discountPercent > 0 || discountedPrice < originalPrice;
      const formattedOriginal = this.formatMoney(originalPrice * 100);
      const formattedDiscounted = this.formatMoney(discountedPrice * 100);
      const imageSrc = product.productImageSrc || variant.imageSrc || '';

      // Get metafields for this product
      const metafields = this.productMetafields.get(product.productHandle) || {};

      card.innerHTML = `
        <div class="bundle-product-card__clickable" role="button" tabindex="0" aria-label="View details for ${this.escapeHtml(product.productTitle)}">
          <div class="bundle-product-card__image-wrapper">
            <img
              src="${imageSrc}"
              alt="${this.escapeHtml(product.productTitle)}"
              class="bundle-product-card__image"
              loading="lazy"
            >
            <span class="bundle-product-card__expand-icon" aria-hidden="true">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 3H13V7M13 3L9.5 6.5M7 13H3V9M3 13L6.5 9.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <span class="bundle-product-card__view-details">See details</span>
          </div>
          <div class="bundle-product-card__info">
            <h3 class="bundle-product-card__title">${this.escapeHtml(product.productTitle)}</h3>
            <div class="bundle-product-card__price">
              ${hasDiscount ? `
                <span class="bundle-product-card__price-original">${formattedOriginal}</span>
                <span class="bundle-product-card__price-sale">${formattedDiscounted}</span>
              ` : `
                <span class="bundle-product-card__price-current">${formattedOriginal}</span>
              `}
            </div>
            <span class="bundle-product-card__view-details-inline">See details</span>
          </div>
        </div>
        <div class="bundle-product-card__quantity-controls">
          <button type="button" class="bundle-product-card__qty-btn bundle-product-card__qty-minus" data-action="decrease" aria-label="Remove one">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 8H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
          <span class="bundle-product-card__qty-value">0</span>
          <button type="button" class="bundle-product-card__qty-btn bundle-product-card__qty-plus" data-action="increase" aria-label="Add one">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      `;

      // Bind quantity control events
      const minusBtn = card.querySelector('.bundle-product-card__qty-minus');
      const plusBtn = card.querySelector('.bundle-product-card__qty-plus');

      minusBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.decreaseQuantity(variant.platformId);
      });

      plusBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.increaseQuantity(product, variant);
      });

      // Clickable area opens drawer
      const clickableArea = card.querySelector('.bundle-product-card__clickable');
      clickableArea.addEventListener('click', () => {
        this.openDrawer(product, variant);
      });
      clickableArea.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.openDrawer(product, variant);
        }
      });

      this.grid.appendChild(card);
    });

    // Append free gift cards from template (if any)
    const giftTemplate = document.getElementById('bundle-gift-cards-template');
    if (giftTemplate) {
      const giftCards = giftTemplate.content.cloneNode(true);
      this.grid.appendChild(giftCards);
    }

    // Initial UI update
    this.updateUI();
  }

  renderUpsells() {
    if (!this.upsellsGrid || this.oneTimeUpsells.length === 0) return;

    // Update header text
    if (this.upsellsHeader) {
      this.upsellsHeader.textContent = this.upsellSettings.header || 'Choose Your Add-on';
    }

    // Update subheader text (from Skio API otuSubheader setting)
    const upsellsSubheader = this.container.querySelector('.bundle-builder__upsells-subheader');
    if (upsellsSubheader) {
      if (this.upsellSettings.subheader) {
        upsellsSubheader.textContent = this.upsellSettings.subheader;
        upsellsSubheader.style.display = '';
      } else {
        upsellsSubheader.style.display = 'none';
      }
    }

    // Clear existing content
    this.upsellsGrid.innerHTML = '';

    this.oneTimeUpsells.forEach((product) => {
      // Get the first variant (or use variant order if specified)
      let variant = product.productVariants?.[0];
      if (!variant) return;

      const card = document.createElement('div');
      card.className = 'bundle-upsell-card';
      card.dataset.productId = product.productPlatformId;
      card.dataset.variantId = variant.platformId;

      const imageSrc = product.productImageSrc || variant.imageSrc || '';
      const formattedPrice = this.formatMoney(variant.price * 100);

      // Build display title with variant info for specificity (e.g., "Cacao Nibs - 4oz")
      let displayTitle = product.productTitle;
      const variantTitle = variant.productVariantTitle;
      if (variantTitle && variantTitle !== 'Default Title' && !displayTitle.toLowerCase().includes(variantTitle.toLowerCase())) {
        displayTitle = `${product.productTitle} - ${variantTitle}`;
      }

      card.innerHTML = `
        <div class="bundle-upsell-card__image-wrapper">
          <img
            src="${imageSrc}"
            alt="${this.escapeHtml(displayTitle)}"
            class="bundle-upsell-card__image"
            loading="lazy"
          >
          <button type="button" class="bundle-upsell-card__view-details" aria-label="See details for ${this.escapeHtml(product.productTitle)}">See details</button>
        </div>
        <div class="bundle-upsell-card__info">
          <h3 class="bundle-upsell-card__title">${this.escapeHtml(displayTitle)}</h3>
          <p class="bundle-upsell-card__price">${formattedPrice}</p>
        </div>
        <div class="bundle-upsell-card__quantity-controls">
          <button type="button" class="bundle-upsell-card__qty-btn bundle-upsell-card__qty-minus" data-action="decrease" aria-label="Remove one" disabled>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 8H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
          <span class="bundle-upsell-card__qty-value">0</span>
          <button type="button" class="bundle-upsell-card__qty-btn bundle-upsell-card__qty-plus" data-action="increase" aria-label="Add one">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      `;

      // Bind "See details" button
      const detailsBtn = card.querySelector('.bundle-upsell-card__view-details');
      detailsBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.openUpsellDrawer(product, variant);
      });

      // Bind quantity control events
      const minusBtn = card.querySelector('.bundle-upsell-card__qty-minus');
      const plusBtn = card.querySelector('.bundle-upsell-card__qty-plus');

      minusBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.decreaseUpsellQuantity(variant.platformId);
      });

      plusBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.increaseUpsellQuantity(product, variant);
      });

      this.upsellsGrid.appendChild(card);
    });
  }

  increaseUpsellQuantity(product, variant) {
    const variantId = variant.platformId;

    if (this.selectedUpsells.has(variantId)) {
      const item = this.selectedUpsells.get(variantId);
      item.quantity += 1;
    } else {
      this.selectedUpsells.set(variantId, {
        productId: product.productPlatformId,
        variantId: variant.platformId,
        title: product.productTitle,
        price: variant.price,
        image: product.productImageSrc || variant.imageSrc,
        quantity: 1
      });
    }

    this.updateUpsellsUI();
  }

  decreaseUpsellQuantity(variantId) {
    if (!this.selectedUpsells.has(variantId)) return;

    const item = this.selectedUpsells.get(variantId);
    item.quantity -= 1;

    if (item.quantity <= 0) {
      this.selectedUpsells.delete(variantId);
    }

    this.updateUpsellsUI();
  }

  updateUpsellsUI() {
    // Update upsell cards quantity display
    const cards = this.upsellsGrid?.querySelectorAll('.bundle-upsell-card') || [];
    cards.forEach(card => {
      const variantId = card.dataset.variantId;
      const item = this.selectedUpsells.get(variantId);
      const quantity = item ? item.quantity : 0;

      // Update quantity display
      const qtyValue = card.querySelector('.bundle-upsell-card__qty-value');
      if (qtyValue) {
        qtyValue.textContent = quantity;
      }

      // Update minus button disabled state
      const minusBtn = card.querySelector('.bundle-upsell-card__qty-minus');
      if (minusBtn) {
        minusBtn.disabled = quantity === 0;
      }

      // Add selected class
      card.classList.toggle('is-selected', quantity > 0);
    });

    // Show/hide upsells button based on selection
    const upsellsButton = this.container.querySelector('.bundle-builder__add-with-upsells');
    if (upsellsButton) {
      upsellsButton.classList.toggle('is-hidden', this.selectedUpsells.size === 0);
    }

    // Update floating bar upsell slots
    this.updateFloatingUpsells();

    // Update pricing to include upsells
    this.updatePricing();
  }

  updateFloatingUpsells() {
    if (!this.floatingUpsellsContainer) return;

    // Clear existing upsell slots
    this.floatingUpsellsContainer.innerHTML = '';

    // Show/hide container based on whether we have upsells selected
    if (this.selectedUpsells.size === 0) {
      this.floatingUpsellsContainer.style.display = 'none';
      return;
    }

    this.floatingUpsellsContainer.style.display = 'flex';

    // Add a "+" separator before upsells
    const separator = document.createElement('span');
    separator.className = 'bundle-builder__upsell-separator';
    separator.textContent = '+';
    this.floatingUpsellsContainer.appendChild(separator);

    // Add a slot for each selected upsell (flattened by quantity)
    this.selectedUpsells.forEach(item => {
      for (let i = 0; i < item.quantity; i++) {
        const slot = document.createElement('div');
        slot.className = 'bundle-builder__upsell-slot';
        slot.innerHTML = `
          <img src="${item.image || ''}" alt="${this.escapeHtml(item.title)}" class="bundle-builder__upsell-slot-image">
        `;
        this.floatingUpsellsContainer.appendChild(slot);
      }
    });
  }

  updateFloatingGifts() {
    if (!this.floatingGiftsContainer || !this.giftEnabled || this.giftDisplayData.length === 0) return;

    // Check visibility: if subscription-only, only show when subscription is selected
    const shouldShow = !this.giftSubscriptionOnly || this.isSubscription;

    if (!shouldShow) {
      this.floatingGiftsContainer.style.display = 'none';
      return;
    }

    this.floatingGiftsContainer.style.display = 'flex';
    this.floatingGiftsContainer.innerHTML = '';

    // Add a "+" separator before gifts
    const separator = document.createElement('span');
    separator.className = 'bundle-builder__gift-separator';
    separator.textContent = '+';
    this.floatingGiftsContainer.appendChild(separator);

    // Add a slot for each gift product
    this.giftDisplayData.forEach(gift => {
      const slot = document.createElement('div');
      slot.className = 'bundle-builder__gift-slot';
      slot.innerHTML = `
        <img src="${gift.image || ''}" alt="${this.escapeHtml(gift.title)}" class="bundle-builder__gift-slot-image">
        <span class="bundle-builder__gift-slot-badge">Free</span>
      `;
      this.floatingGiftsContainer.appendChild(slot);
    });
  }

  hasUpsells() {
    return this.oneTimeUpsells && this.oneTimeUpsells.length > 0;
  }

  showUpsellsStep() {
    if (!this.hasUpsells()) {
      // No upsells, go directly to cart
      this.addToCart();
      return;
    }

    this.currentStep = 'upsells';

    // Render upsells if not already rendered
    if (this.upsellsGrid && this.upsellsGrid.children.length === 0) {
      this.renderUpsells();
    }

    // Hide selection step, show upsells step
    if (this.selectionStep) {
      this.selectionStep.style.display = 'none';
    }
    if (this.upsellsStep) {
      this.upsellsStep.style.display = '';
    }

    // Add class to container for full-width layout
    this.container.classList.add('bundle-builder--upsells-active');

    // Hide other page content (product info sidebar, mobile header)
    // Keep floating bar visible to show bundle selections
    const productInfoWrapper = this.container.querySelector('.product__info-wrapper');
    const mobileHeader = this.container.querySelector('.bundle-builder__mobile-header');
    if (productInfoWrapper) productInfoWrapper.style.display = 'none';
    if (mobileHeader) mobileHeader.style.display = 'none';
    // Floating bar stays visible - don't hide it

    // Update floating bar for upsells step context
    this.updateFloatingBarForStep();

    // Ensure body can scroll (in case something added overflow-hidden)
    document.body.classList.remove('overflow-hidden');

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  showSelectionStep() {
    this.currentStep = 'selection';

    // Remove full-width layout class
    this.container.classList.remove('bundle-builder--upsells-active');

    // Show selection step, hide upsells step
    if (this.selectionStep) {
      this.selectionStep.style.display = '';
    }
    if (this.upsellsStep) {
      this.upsellsStep.style.display = 'none';
    }

    // Show other page content (product info sidebar, mobile header, floating bar)
    const productInfoWrapper = this.container.querySelector('.product__info-wrapper');
    const mobileHeader = this.container.querySelector('.bundle-builder__mobile-header');
    if (productInfoWrapper) productInfoWrapper.style.display = '';
    if (mobileHeader) mobileHeader.style.display = '';
    if (this.floatingBar) this.floatingBar.style.display = '';

    // Update floating bar for selection step
    this.updateFloatingBarForStep();

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  updateFloatingBarForStep() {
    const totalQuantity = this.getTotalQuantity();
    const isComplete = totalQuantity === this.maxSelections;

    // Hide selection status (e.g., "2/2 selected") when bundle is complete
    if (this.floatingSelectionStatus) {
      this.floatingSelectionStatus.style.display = isComplete ? 'none' : '';
    }

    // Update next-tier incentive message
    this.updateTierIncentive(totalQuantity);

    if (this.floatingAddButton) {
      const text = this.floatingAddButton.querySelector('.bundle-builder__floating-add-text');

      if (this.currentStep === 'upsells') {
        // On upsells step - bundle is complete, show "Add to Cart" for combined add
        const addToCartText = this.isSubscription ? 'Add Subscription to Cart' : 'Add to cart';
        if (text) text.textContent = addToCartText;
        this.floatingAddButton.disabled = false;
      } else if (isComplete) {
        // Selection step, box is complete - show "Continue" if upsells available, otherwise "Add to Cart"
        if (this.hasUpsells()) {
          if (text) text.textContent = 'Continue';
        } else {
          const addToCartText = this.isSubscription ? 'Add Subscription to Cart' : 'Add to cart';
          if (text) text.textContent = addToCartText;
        }
        this.floatingAddButton.disabled = false;
      } else {
        // Box not complete
        const remaining = this.maxSelections - totalQuantity;
        if (text) text.textContent = `Select ${remaining} more`;
        this.floatingAddButton.disabled = true;
      }
    }

    // Update pricing for current step
    this.updatePricing();
  }

  async handlePrimaryButtonClick() {
    const totalQuantity = this.getTotalQuantity();
    const isComplete = totalQuantity === this.maxSelections;

    if (!isComplete) {
      return; // Button should be disabled, but double-check
    }

    if (this.currentStep === 'selection') {
      // If upsells available, show upsells step (defer cart add until after upsells)
      if (this.hasUpsells()) {
        this.showUpsellsStep();
      } else {
        // No upsells - add bundle directly to cart
        await this.addBundleToCart();
      }
    } else if (this.currentStep === 'upsells') {
      // On upsells step - add bundle + any selected upsells to cart
      await this.addToCart();
    }
  }

  async fetchProductMetafields() {
    const fetchPromises = this.products.map(async (product) => {
      try {
        const response = await fetch(`/products/${product.productHandle}.js`);
        if (response.ok) {
          const productData = await response.json();
          if (productData.metafields?.my_fields) {
            this.productMetafields.set(product.productHandle, productData.metafields.my_fields);
          }
        }
      } catch (error) {
        // Metafields are optional, silently continue
      }
    });

    await Promise.all(fetchPromises);
  }

  getTotalQuantity() {
    let total = 0;
    this.selectedProducts.forEach(item => {
      total += item.quantity;
    });
    return total;
  }

  increaseQuantity(product, variant) {
    const variantId = variant.platformId;
    const currentTotal = this.getTotalQuantity();

    if (currentTotal >= this.maxSelections) {
      // Auto-advance to next available size if possible
      const nextSize = this.availableSizes.find(s => s > this.maxSelections);
      if (nextSize) {
        this.changeBoxSize(nextSize);
      } else {
        return;
      }
    }

    if (this.selectedProducts.has(variantId)) {
      // Increase existing quantity
      const item = this.selectedProducts.get(variantId);
      item.quantity += 1;
    } else {
      // Add new product
      this.selectedProducts.set(variantId, {
        productId: product.productPlatformId,
        variantId: variant.platformId,
        title: product.productTitle,
        price: variant.price,
        image: product.productImageSrc || variant.imageSrc,
        quantity: 1
      });
    }

    this.updateUI();
  }

  decreaseQuantity(variantId) {
    if (!this.selectedProducts.has(variantId)) return;

    const item = this.selectedProducts.get(variantId);
    item.quantity -= 1;

    if (item.quantity <= 0) {
      this.selectedProducts.delete(variantId);
    }

    this.updateUI();
  }

  removeProduct(variantId) {
    this.selectedProducts.delete(variantId);
    this.updateUI();
  }

  updateUI() {
    const totalQuantity = this.getTotalQuantity();

    // Update count displays
    this.countElements.forEach(el => {
      el.textContent = totalQuantity;
    });

    // Update product cards
    const cards = this.grid.querySelectorAll('.bundle-product-card');
    cards.forEach(card => {
      const variantId = card.dataset.variantId;
      const item = this.selectedProducts.get(variantId);
      const quantity = item ? item.quantity : 0;
      const isSelected = quantity > 0;

      // Update quantity display
      const qtyValue = card.querySelector('.bundle-product-card__qty-value');
      if (qtyValue) {
        qtyValue.textContent = quantity;
      }

      // Update card state
      card.classList.toggle('is-selected', isSelected);
      card.classList.toggle('has-quantity', isSelected);
      card.dataset.quantity = quantity;

      // Update minus button disabled state
      const minusBtn = card.querySelector('.bundle-product-card__qty-minus');
      if (minusBtn) {
        minusBtn.disabled = !isSelected;
      }

      // Update plus button disabled state (only at largest available size)
      const plusBtn = card.querySelector('.bundle-product-card__qty-plus');
      if (plusBtn) {
        const largestSize = this.availableSizes.length > 0 ? this.availableSizes[this.availableSizes.length - 1] : this.maxSelections;
        plusBtn.disabled = totalQuantity >= largestSize;
      }
    });

    // Update floating bar slots
    this.updateFloatingSlots();

    // Update floating bar gift slots
    this.updateFloatingGifts();

    // Update floating bar button (step-aware)
    this.updateFloatingBarForStep();

    // Update buy button block (in product info area)
    const isComplete = totalQuantity === this.maxSelections;
    const remaining = this.maxSelections - totalQuantity;

    if (this.buyButtonBlock) {
      const text = this.buyButtonBlock.querySelector('.bundle-builder__buy-button-text');

      if (isComplete) {
        // Show "Continue" if upsells available, otherwise "Add to Cart"
        if (this.hasUpsells() && this.currentStep === 'selection') {
          if (text) text.textContent = 'CONTINUE';
        } else {
          const addToCartText = this.isSubscription ? 'ADD SUBSCRIPTION TO CART' : 'ADD TO CART';
          if (text) text.textContent = addToCartText;
        }
        this.buyButtonBlock.disabled = false;
      } else {
        if (text) text.textContent = `Select ${remaining} more bag${remaining !== 1 ? 's' : ''}`;
        this.buyButtonBlock.disabled = true;
      }
    }

    // Hide discount callouts when subscription is active (discounts don't stack)
    this.container.querySelectorAll('.bundle-builder__size-discount').forEach(badge => {
      badge.hidden = this.isSubscription;
    });
    const incentiveEl = this.container.querySelector('.bundle-builder__tier-incentive');
    if (incentiveEl && this.isSubscription) incentiveEl.style.display = 'none';

    // Update plan picker prices
    this.updatePlanPickerPrices();
  }

  updateFloatingSlots() {
    // Flatten selections into an array based on quantities
    const flatSelections = [];
    this.selectedProducts.forEach(item => {
      for (let i = 0; i < item.quantity; i++) {
        flatSelections.push(item);
      }
    });

    // Update floating bar slots
    this.floatingSlots.forEach((slot, index) => {
      this.updateSlotDisplay(slot, flatSelections[index]);
    });

    // Update info area slots
    this.infoSlots.forEach((slot, index) => {
      this.updateSlotDisplay(slot, flatSelections[index]);
    });
  }

  updateSlotDisplay(slot, product) {
    const emptyEl = slot.querySelector('.bundle-builder__slot-empty');
    const filledEl = slot.querySelector('.bundle-builder__slot-filled');
    const img = filledEl?.querySelector('img');

    if (product) {
      if (emptyEl) emptyEl.style.display = 'none';
      if (filledEl) {
        filledEl.style.display = '';
        if (img) {
          img.src = product.image || '';
          img.alt = product.title;
        }
      }
    } else {
      if (emptyEl) emptyEl.style.display = '';
      if (filledEl) filledEl.style.display = 'none';
    }
  }

  updateSlots(slots, isFooter) {
    // Flatten selections into an array based on quantities
    const flatSelections = [];
    this.selectedProducts.forEach(item => {
      for (let i = 0; i < item.quantity; i++) {
        flatSelections.push(item);
      }
    });

    slots.forEach((slot, index) => {
      const product = flatSelections[index];
      const emptyEl = slot.querySelector(isFooter ? '.bundle-builder__footer-slot-empty' : '.bundle-builder__slot-empty');
      const filledEl = slot.querySelector(isFooter ? '.bundle-builder__footer-slot-filled' : '.bundle-builder__slot-filled');
      const img = filledEl?.querySelector('img');

      if (product) {
        if (emptyEl) emptyEl.style.display = 'none';
        if (filledEl) {
          filledEl.style.display = '';
          if (img) {
            img.src = product.image || '';
            img.alt = product.title;
          }
        }
      } else {
        if (emptyEl) emptyEl.style.display = '';
        if (filledEl) filledEl.style.display = 'none';
      }
    });
  }

  updatePricing() {
    // Calculate totals - sum price * quantity for each selection
    let originalTotal = 0;
    this.selectedProducts.forEach(item => {
      originalTotal += item.price * item.quantity;
    });

    // Calculate upsells total (no discount applied to upsells)
    let upsellsTotal = 0;
    this.selectedUpsells.forEach(item => {
      upsellsTotal += item.price * item.quantity;
    });

    let discountedTotal = originalTotal;
    let discountPercent = 0;
    let discountAmount = 0;

    // Check for tier discounts (same % for all box sizes)
    if (this.boxData?.percentDiscountTier) {
      const tierDiscount = this.getTierDiscount(this.boxData.percentDiscountTier);
      if (tierDiscount) {
        discountPercent = parseFloat(tierDiscount);
        discountedTotal = originalTotal * (1 - discountPercent / 100);
      }
    }

    // Check for fixed discount tier
    if (this.boxData?.fixedDiscountTier && discountPercent === 0) {
      const tierDiscount = this.getTierDiscount(this.boxData.fixedDiscountTier);
      if (tierDiscount) {
        discountAmount = parseFloat(tierDiscount);
        discountedTotal = originalTotal - discountAmount;
      }
    }

    // Additional subscription discount if applicable
    if (this.isSubscription && this.boxData?.sellingPlanGroup && this.selectedSellingPlan) {
      const subscriptionOverride = this.planPickerConfig?.subscriptionDiscountOverride || 0;

      if (subscriptionOverride > 0) {
        // Use override: apply this percentage to the ORIGINAL price (not the tier-discounted price)
        discountedTotal = originalTotal * (1 - subscriptionOverride / 100);
        discountPercent = subscriptionOverride; // Override the display discount too
      } else {
        // Default Skio behavior: apply selling plan discount to the tier-discounted price
        const plan = this.boxData.sellingPlanGroup[this.selectedSellingPlan];
        if (plan) {
          if (plan.priceAdjustmentType === 'PERCENTAGE') {
            const subDiscount = plan.priceAdjustmentAmount || 0;
            discountedTotal = discountedTotal * (1 - subDiscount / 100);
            discountPercent = discountPercent + subDiscount; // Combine for display
          } else if (plan.priceAdjustmentType === 'FIXED_AMOUNT') {
            discountedTotal = discountedTotal - (plan.priceAdjustmentAmount || 0);
          }
        }
      }
    }

    const hasDiscount = discountPercent > 0 || discountAmount > 0;

    // Include upsells in final totals (upsells are at full price, no discount)
    const finalOriginal = originalTotal + upsellsTotal;
    const finalDiscounted = discountedTotal + upsellsTotal;

    const formattedOriginal = this.formatMoney(finalOriginal * 100);
    const formattedDiscounted = this.formatMoney(finalDiscounted * 100);

    // Update floating bar button price (inside the button)
    if (this.floatingAddCurrent) {
      if (finalDiscounted > 0) {
        this.floatingAddCurrent.textContent = formattedDiscounted;
        // Show compare price if there's a discount OR if upsells make original different
        if ((hasDiscount || upsellsTotal > 0) && this.floatingAddCompare) {
          this.floatingAddCompare.textContent = hasDiscount ? formattedOriginal : '';
        } else if (this.floatingAddCompare) {
          this.floatingAddCompare.textContent = '';
        }
      } else {
        this.floatingAddCurrent.textContent = '';
        if (this.floatingAddCompare) this.floatingAddCompare.textContent = '';
      }
    }

    // Update main price display (in blocks area)
    if (this.mainPriceDisplay) {
      const totalQuantity = this.getTotalQuantity();
      const isComplete = totalQuantity === this.maxSelections;

      if (isComplete && finalOriginal > 0) {
        if (hasDiscount) {
          // Show original price crossed out, then sale price
          this.mainPriceDisplay.innerHTML = `
            <span class="price-item price-item--regular">${formattedOriginal}</span>
            <span class="price-item price-item--sale">${formattedDiscounted}</span>
          `;
        } else {
          this.mainPriceDisplay.innerHTML = `<span class="price-item">${formattedOriginal}</span>`;
        }
      } else {
        // Show nothing when bundle is incomplete
        this.mainPriceDisplay.innerHTML = '';
      }
    }

    // Also update any elements with skio-external-price attribute
    document.querySelectorAll('[skio-external-price]').forEach(el => {
      if (el !== this.mainPriceDisplay && finalDiscounted > 0) {
        if (hasDiscount) {
          el.innerHTML = `
            <span class="price-item price-item--regular">${formattedOriginal}</span>
            <span class="price-item price-item--sale">${formattedDiscounted}</span>
          `;
        } else {
          el.textContent = formattedOriginal;
        }
      }
    });

    // Update upsells step "Add to Cart" button price (just upsells total, bundle already added)
    const upsellsPriceEl = this.container.querySelector('.bundle-builder__add-upsells-price');
    if (upsellsPriceEl) {
      if (upsellsTotal > 0) {
        const formattedUpsellsTotal = this.formatMoney(upsellsTotal * 100);
        upsellsPriceEl.textContent = formattedUpsellsTotal;
      } else {
        upsellsPriceEl.textContent = '';
      }
    }
  }

  async openDrawer(product, variant) {
    if (!this.drawer || !this.drawerBody) return;

    // Calculate price with discount (same logic as cards)
    const originalPrice = variant.price;
    let discountedPrice = originalPrice;
    let discountPercent = 0;

    if (this.boxData?.percentDiscountTier) {
      const tierDiscount = this.getTierDiscount(this.boxData.percentDiscountTier);
      if (tierDiscount) {
        discountPercent = parseFloat(tierDiscount);
        discountedPrice = originalPrice * (1 - discountPercent / 100);
      }
    } else if (this.boxData?.fixedDiscountTier) {
      const tierDiscount = this.getTierDiscount(this.boxData.fixedDiscountTier);
      if (tierDiscount) {
        const perItemDiscount = parseFloat(tierDiscount) / this.maxSelections;
        discountedPrice = originalPrice - perItemDiscount;
      }
    }

    const hasDiscount = discountPercent > 0 || discountedPrice < originalPrice;
    const formattedOriginal = this.formatMoney(originalPrice * 100);
    const formattedDiscounted = this.formatMoney(discountedPrice * 100);

    const isSelected = this.selectedProducts.has(variant.platformId);
    const fallbackImageSrc = product.productImageSrc || variant.imageSrc || '';

    const priceHtml = hasDiscount
      ? `<span class="bundle-drawer__price-original">${formattedOriginal}</span> <span class="bundle-drawer__price-sale">${formattedDiscounted}</span>`
      : formattedOriginal;

    // Extract numeric product ID for Okendo (handle GID format)
    let okendoProductId = product.productPlatformId;
    if (typeof okendoProductId === 'string' && okendoProductId.includes('gid://')) {
      okendoProductId = okendoProductId.split('/').pop();
    }

    // Show loading state first
    this.drawerBody.innerHTML = `
      <div class="bundle-drawer__scroll-area">
        <div class="bundle-drawer__product">
          <div class="bundle-drawer__image bundle-drawer__image--loading">
            <div class="bundle-builder__spinner"></div>
          </div>
          <h2 class="bundle-drawer__title">${this.escapeHtml(product.productTitle)}</h2>
          <div class="bundle-drawer__star-rating" data-oke-star-rating data-oke-reviews-product-id="shopify-${okendoProductId}"></div>
          <p class="bundle-drawer__price">${priceHtml}</p>
          <div class="bundle-drawer__description">
            <p>Loading product details...</p>
          </div>
          <div class="bundle-drawer__reviews" data-oke-carousel data-oke-reviews-product-id="shopify-${okendoProductId}"></div>
        </div>
      </div>
      <div class="bundle-drawer__footer">
        <button type="button" class="bundle-drawer__button" data-variant-id="${variant.platformId}" data-is-selected="${isSelected}">
          ${isSelected ? 'Selected ✓' : 'Select This Flavor'}
        </button>
      </div>
    `;

    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Trigger Okendo to initialize the star rating widget for dynamically added content
    this.initOkendoWidgets();

    // Fetch full product data from Shopify (includes images and description)
    let productImages = [fallbackImageSrc];
    let productDescription = '';

    try {
      const response = await fetch(`/products/${product.productHandle}.js`);
      if (response.ok) {
        const productData = await response.json();
        if (productData.images && productData.images.length > 0) {
          productImages = productData.images;
        }
        if (productData.description) {
          productDescription = productData.description;
        }
      }
    } catch (error) {
      // Product details are optional - use fallback image
    }

    // Render the image carousel (or single image if only one)
    const imageContainerEl = this.drawerBody.querySelector('.bundle-drawer__image');
    if (imageContainerEl) {
      imageContainerEl.classList.remove('bundle-drawer__image--loading');

      if (productImages.length > 1) {
        // Render carousel with multiple images
        imageContainerEl.innerHTML = this.renderDrawerCarousel(productImages, product.productTitle);
        // Initialize the carousel after rendering
        this.initDrawerCarousel(imageContainerEl);
      } else {
        // Single image - no carousel needed
        imageContainerEl.innerHTML = `<img src="${productImages[0]}" alt="${this.escapeHtml(product.productTitle)}">`;
      }
    }

    // Update description
    const descriptionEl = this.drawerBody.querySelector('.bundle-drawer__description');
    if (descriptionEl) {
      if (productDescription) {
        descriptionEl.innerHTML = productDescription;
      } else {
        descriptionEl.innerHTML = '';
      }
    }

    // Bind drawer select button
    const drawerBtn = this.drawerBody.querySelector('.bundle-drawer__button');
    if (drawerBtn) {
      // Hide button if at largest available size and this item isn't already selected
      const totalQuantity = this.getTotalQuantity();
      const largestSize = this.availableSizes.length > 0 ? this.availableSizes[this.availableSizes.length - 1] : this.maxSelections;
      const atAbsoluteMax = totalQuantity >= largestSize;

      if (atAbsoluteMax && !isSelected) {
        drawerBtn.style.display = 'none';
      }

      drawerBtn.addEventListener('click', () => {
        const wasSelectedOnOpen = drawerBtn.dataset.isSelected === 'true';

        if (wasSelectedOnOpen) {
          // Deselect: remove entirely and close drawer
          this.removeProduct(variant.platformId);
          this.closeDrawer();
          return;
        }

        // Add one to quantity and close drawer
        this.increaseQuantity(product, variant);
        this.closeDrawer();
      });
    }
  }

  renderDrawerCarousel(images, productTitle) {
    const slidesHtml = images.map((imageSrc, index) => `
      <li class="bundle-drawer__carousel-slide">
        <img
          src="${imageSrc}"
          alt="${this.escapeHtml(productTitle)} - Image ${index + 1}"
          loading="${index === 0 ? 'eager' : 'lazy'}"
        >
      </li>
    `).join('');

    return `
      <div class="bundle-drawer__carousel">
        <ul class="bundle-drawer__carousel-track">
          ${slidesHtml}
        </ul>
        <div class="bundle-drawer__carousel-nav">
          <button type="button" class="bundle-drawer__carousel-btn bundle-drawer__carousel-prev" aria-label="Previous image" disabled>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <span class="bundle-drawer__carousel-counter">
            <span class="bundle-drawer__carousel-current">1</span>
            /
            <span class="bundle-drawer__carousel-total">${images.length}</span>
          </span>
          <button type="button" class="bundle-drawer__carousel-btn bundle-drawer__carousel-next" aria-label="Next image">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    `;
  }

  initDrawerCarousel(container) {
    const track = container.querySelector('.bundle-drawer__carousel-track');
    const prevBtn = container.querySelector('.bundle-drawer__carousel-prev');
    const nextBtn = container.querySelector('.bundle-drawer__carousel-next');
    const currentEl = container.querySelector('.bundle-drawer__carousel-current');
    const slides = container.querySelectorAll('.bundle-drawer__carousel-slide');

    if (!track || !prevBtn || !nextBtn || slides.length === 0) return;

    let currentIndex = 0;
    const totalSlides = slides.length;

    const updateCarousel = () => {
      // Scroll to the current slide
      const slideWidth = slides[0].offsetWidth;
      track.scrollTo({ left: slideWidth * currentIndex, behavior: 'smooth' });

      // Update counter
      if (currentEl) {
        currentEl.textContent = currentIndex + 1;
      }

      // Update button states
      prevBtn.disabled = currentIndex === 0;
      nextBtn.disabled = currentIndex === totalSlides - 1;
    };

    prevBtn.addEventListener('click', () => {
      if (currentIndex > 0) {
        currentIndex--;
        updateCarousel();
      }
    });

    nextBtn.addEventListener('click', () => {
      if (currentIndex < totalSlides - 1) {
        currentIndex++;
        updateCarousel();
      }
    });

    // Handle scroll snap - update currentIndex when user swipes
    let scrollTimeout;
    track.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const slideWidth = slides[0].offsetWidth;
        const newIndex = Math.round(track.scrollLeft / slideWidth);
        if (newIndex !== currentIndex && newIndex >= 0 && newIndex < totalSlides) {
          currentIndex = newIndex;
          if (currentEl) currentEl.textContent = currentIndex + 1;
          prevBtn.disabled = currentIndex === 0;
          nextBtn.disabled = currentIndex === totalSlides - 1;
        }
      }, 50);
    });

    // Initial state
    updateCarousel();
  }

  closeDrawer() {
    if (!this.drawer) return;
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  // Open drawer for upsell products (no bundle discount, uses quantity controls)
  async openUpsellDrawer(product, variant) {
    if (!this.drawer || !this.drawerBody) return;

    // Upsells are at full price (no discount)
    const formattedPrice = this.formatMoney(variant.price * 100);

    // Build display title with variant info
    let displayTitle = product.productTitle;
    const variantTitle = variant.productVariantTitle;
    if (variantTitle && variantTitle !== 'Default Title' && !displayTitle.toLowerCase().includes(variantTitle.toLowerCase())) {
      displayTitle = `${product.productTitle} - ${variantTitle}`;
    }

    const fallbackImageSrc = product.productImageSrc || variant.imageSrc || '';

    // Extract numeric product ID for Okendo
    let okendoProductId = product.productPlatformId;
    if (typeof okendoProductId === 'string' && okendoProductId.includes('gid://')) {
      okendoProductId = okendoProductId.split('/').pop();
    }

    // Get current quantity from selectedUpsells
    const currentItem = this.selectedUpsells.get(variant.platformId);
    const currentQty = currentItem ? currentItem.quantity : 0;

    // Show loading state first
    this.drawerBody.innerHTML = `
      <div class="bundle-drawer__scroll-area">
        <div class="bundle-drawer__product">
          <div class="bundle-drawer__image bundle-drawer__image--loading">
            <div class="bundle-builder__spinner"></div>
          </div>
          <h2 class="bundle-drawer__title">${this.escapeHtml(displayTitle)}</h2>
          <div class="bundle-drawer__star-rating" data-oke-star-rating data-oke-reviews-product-id="shopify-${okendoProductId}"></div>
          <p class="bundle-drawer__price">${formattedPrice}</p>
          <div class="bundle-drawer__description">
            <p>Loading product details...</p>
          </div>
          <div class="bundle-drawer__reviews" data-oke-carousel data-oke-reviews-product-id="shopify-${okendoProductId}"></div>
        </div>
      </div>
      <div class="bundle-drawer__footer bundle-drawer__footer--upsell">
        <div class="bundle-drawer__upsell-controls">
          <button type="button" class="bundle-drawer__qty-btn bundle-drawer__qty-minus" data-action="decrease" aria-label="Remove one" ${currentQty === 0 ? 'disabled' : ''}>
            <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 8H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
          <span class="bundle-drawer__qty-value">${currentQty}</span>
          <button type="button" class="bundle-drawer__qty-btn bundle-drawer__qty-plus" data-action="increase" aria-label="Add one">
            <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <button type="button" class="bundle-drawer__button bundle-drawer__button--done">
          Done
        </button>
      </div>
    `;

    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Trigger Okendo widgets
    this.initOkendoWidgets();

    // Fetch full product data
    let productImages = [fallbackImageSrc];
    let productDescription = '';

    try {
      const response = await fetch(`/products/${product.productHandle}.js`);
      if (response.ok) {
        const productData = await response.json();
        if (productData.images && productData.images.length > 0) {
          productImages = productData.images;
        }
        if (productData.description) {
          productDescription = productData.description;
        }
      }
    } catch (error) {
      // Product details are optional
    }

    // Update image
    const imageContainerEl = this.drawerBody.querySelector('.bundle-drawer__image');
    if (imageContainerEl) {
      imageContainerEl.classList.remove('bundle-drawer__image--loading');
      if (productImages.length > 1) {
        imageContainerEl.innerHTML = this.renderDrawerCarousel(productImages, product.productTitle);
        this.initDrawerCarousel(imageContainerEl);
      } else {
        imageContainerEl.innerHTML = `<img src="${productImages[0]}" alt="${this.escapeHtml(product.productTitle)}">`;
      }
    }

    // Update description
    const descriptionEl = this.drawerBody.querySelector('.bundle-drawer__description');
    if (descriptionEl) {
      descriptionEl.innerHTML = productDescription || '';
    }

    // Bind quantity controls in drawer
    const minusBtn = this.drawerBody.querySelector('.bundle-drawer__qty-minus');
    const plusBtn = this.drawerBody.querySelector('.bundle-drawer__qty-plus');
    const qtyValue = this.drawerBody.querySelector('.bundle-drawer__qty-value');

    minusBtn.addEventListener('click', () => {
      this.decreaseUpsellQuantity(variant.platformId);
      const item = this.selectedUpsells.get(variant.platformId);
      const qty = item ? item.quantity : 0;
      qtyValue.textContent = qty;
      minusBtn.disabled = qty === 0;
    });

    plusBtn.addEventListener('click', () => {
      this.increaseUpsellQuantity(product, variant);
      const item = this.selectedUpsells.get(variant.platformId);
      const qty = item ? item.quantity : 0;
      qtyValue.textContent = qty;
      minusBtn.disabled = qty === 0;
    });

    // Bind done button
    const doneBtn = this.drawerBody.querySelector('.bundle-drawer__button--done');
    doneBtn.addEventListener('click', () => {
      this.closeDrawer();
    });
  }

  async getExistingGiftVariantIds() {
    try {
      const resp = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
      const cart = await resp.json();
      const giftIds = new Set(
        cart.items
          .filter(item => item.properties && String(item.properties._bundleGift) === 'true')
          .map(item => String(item.variant_id))
      );
      return giftIds;
    } catch (e) {
      return new Set();
    }
  }

  // Add just the bundle items to cart (called when no upsells, or user skips upsells)
  async addBundleToCart() {
    const totalQuantity = this.getTotalQuantity();
    if (totalQuantity !== this.maxSelections) {
      return false;
    }

    // Show loading state
    if (this.floatingAddButton) {
      this.floatingAddButton.disabled = true;
      const text = this.floatingAddButton.querySelector('.bundle-builder__floating-add-text');
      if (text) text.textContent = 'Adding...';
    }

    if (this.buyButtonBlock) {
      this.buyButtonBlock.disabled = true;
      const text = this.buyButtonBlock.querySelector('.bundle-builder__buy-button-text');
      if (text) text.textContent = 'Adding...';
    }

    try {
      const boxId = this.boxData?.boxId || this.boxProductId;
      const items = [];

      // Build bundle items array
      this.selectedProducts.forEach(product => {
        let variantId = product.variantId;
        if (typeof variantId === 'string' && variantId.includes('gid://')) {
          variantId = variantId.split('/').pop();
        }

        const properties = {
          '_dynamicBoxId': boxId,
          '_dynamicBoxIndex': '0',
          '_bundleProductId': this.boxProductId,
          '_bundleItem': 'true'
        };

        if (this.isSubscription && this.selectedSellingPlan) {
          properties['_dynamicBoxSellingPlan'] = this.selectedSellingPlan;
        }

        const item = {
          id: parseInt(variantId, 10),
          quantity: product.quantity,
          properties
        };

        // Add selling plan for subscription
        if (this.isSubscription && this.selectedSellingPlan && this.boxData?.sellingPlanGroup) {
          const planGroup = this.boxData.sellingPlanGroup[this.selectedSellingPlan];
          let sellingPlanId = null;

          let lookupId = product.variantId;
          if (typeof lookupId === 'string' && lookupId.includes('gid://')) {
            lookupId = lookupId.split('/').pop();
          }

          const hasVariantMapping = planGroup?.variantMapping && Object.keys(planGroup.variantMapping).length > 0;

          if (hasVariantMapping) {
            const mapping = planGroup.variantMapping[lookupId] ||
                           planGroup.variantMapping[product.variantId] ||
                           planGroup.variantMapping[`gid://shopify/ProductVariant/${lookupId}`];

            if (mapping) {
              sellingPlanId = typeof mapping === 'object' ? mapping.planId : mapping;
            }
          }

          if (sellingPlanId) {
            if (typeof sellingPlanId === 'string' && sellingPlanId.includes('gid://')) {
              sellingPlanId = sellingPlanId.split('/').pop();
            }
            item.selling_plan = parseInt(sellingPlanId, 10);
          }
        }

        items.push(item);
      });

      // Add free gift items if enabled (skip any already in cart)
      if (this.giftEnabled && this.giftVariantIds.length > 0) {
        const shouldAddGift = !this.giftSubscriptionOnly || this.isSubscription;

        if (shouldAddGift) {
          const existingGifts = await this.getExistingGiftVariantIds();
          this.giftVariantIds.forEach(giftVariantId => {
            if (existingGifts.has(String(giftVariantId))) return;
            const giftItem = {
              id: parseInt(giftVariantId, 10),
              quantity: 1,
              properties: {
                '_dynamicBoxId': boxId,
                '_dynamicBoxIndex': '0',
                '_bundleGift': 'true',
                '_bundleProductId': this.boxProductId,
                'Gift': 'Free'
              }
            };

            if (this.isSubscription && this.selectedSellingPlan) {
              giftItem.selling_plan = parseInt(this.selectedSellingPlan, 10);
            }

            items.push(giftItem);
          });
        }
      }

      // Request cart sections in the response so we can update the drawer
      const sectionsToFetch = ['cart-drawer', 'cart-icon-bubble'];
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items,
          sections: sectionsToFetch
        })
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || responseData.description || `Cart Error: ${response.status}`);
      }

      // Clear bundle selections
      this.selectedProducts.clear();

      // Update cart sections (icon bubble and drawer contents)
      if (responseData.sections) {
        // Update cart icon bubble
        if (responseData.sections['cart-icon-bubble']) {
          const cartIconBubble = document.getElementById('cart-icon-bubble');
          if (cartIconBubble) {
            cartIconBubble.innerHTML = new DOMParser()
              .parseFromString(responseData.sections['cart-icon-bubble'], 'text/html')
              .querySelector('.shopify-section').innerHTML;
          }
        }

        // Update cart drawer contents (without opening)
        if (responseData.sections['cart-drawer']) {
          const cartDrawerEl = document.getElementById('CartDrawer');
          if (cartDrawerEl) {
            cartDrawerEl.innerHTML = new DOMParser()
              .parseFromString(responseData.sections['cart-drawer'], 'text/html')
              .querySelector('#CartDrawer').innerHTML;
          }
        }
      }

      // Notify UpCart/other cart apps of the update
      if (typeof window.upcartRefreshCart === 'function') {
        window.upcartRefreshCart();
      }
      // Dispatch cart change event for apps that listen
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:change'));

      // Reset to selection step and open cart drawer
      this.resetToSelectionStep();
      this.openCartDrawer();

      return true;

    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      alert('There was an error adding items to your cart. Please try again.');
      this.updateUI();
      return false;
    } finally {
      // Reset button states
      if (this.floatingAddButton) {
        this.floatingAddButton.disabled = false;
      }
      if (this.buyButtonBlock) {
        this.buyButtonBlock.disabled = false;
      }
      this.updateUI();
    }
  }

  // Add just the upsell items to cart (called from upsells step)
  async addUpsellsToCart() {
    if (this.selectedUpsells.size === 0) {
      // No upsells selected, just finish up
      this.resetToSelectionStep();
      this.openCartDrawer();
      return;
    }

    // Show loading on upsells button
    const upsellsButton = this.container.querySelector('.bundle-builder__add-with-upsells');
    if (upsellsButton) {
      upsellsButton.disabled = true;
      const text = upsellsButton.querySelector('.bundle-builder__add-upsells-text');
      if (text) text.textContent = 'Adding...';
    }

    try {
      const items = [];

      this.selectedUpsells.forEach(upsell => {
        let variantId = upsell.variantId;
        if (typeof variantId === 'string' && variantId.includes('gid://')) {
          variantId = variantId.split('/').pop();
        }

        // Note: Upsells do NOT include _dynamicBoxId to prevent Skio from applying tier discount
        items.push({
          id: parseInt(variantId, 10),
          quantity: upsell.quantity,
          properties: {
            '_bundleUpsell': 'true',
            '_bundleProductId': this.boxProductId
          }
        });
      });

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || responseData.description || `Cart Error: ${response.status}`);
      }

      // Notify UpCart/other cart apps of the update
      if (typeof window.upcartRefreshCart === 'function') {
        window.upcartRefreshCart();
      }
      // Dispatch cart change event for apps that listen
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:change'));

      // Clear upsells and reset UI
      this.selectedUpsells.clear();
      this.resetToSelectionStep();
      this.openCartDrawer();

    } catch (error) {
      console.error('Error adding upsells to cart:', error);
      alert('There was an error adding items to your cart. Please try again.');
    } finally {
      if (upsellsButton) {
        upsellsButton.disabled = false;
        const text = upsellsButton.querySelector('.bundle-builder__add-upsells-text');
        if (text) text.textContent = 'Add to Cart';
      }
    }
  }

  // Reset UI back to selection step
  resetToSelectionStep() {
    this.currentStep = 'selection';
    this.selectedUpsells.clear();

    // Remove upsells-active class from container
    this.container.classList.remove('bundle-builder--upsells-active');

    if (this.selectionStep) this.selectionStep.style.display = '';
    if (this.upsellsStep) this.upsellsStep.style.display = 'none';

    const productInfoWrapper = this.container.querySelector('.product__info-wrapper');
    const mobileHeader = this.container.querySelector('.bundle-builder__mobile-header');
    if (productInfoWrapper) productInfoWrapper.style.display = '';
    if (mobileHeader) mobileHeader.style.display = '';
    if (this.floatingBar) this.floatingBar.style.display = '';

    // Clear floating upsell slots
    this.updateFloatingUpsells();

    this.updateUI();
  }

  async addToCart() {
    const totalQuantity = this.getTotalQuantity();
    if (totalQuantity !== this.maxSelections) {
      return;
    }

    // Disable buttons and show loading
    if (this.floatingAddButton) {
      this.floatingAddButton.disabled = true;
      const text = this.floatingAddButton.querySelector('.bundle-builder__floating-add-text');
      if (text) text.textContent = 'Adding...';
      const spinner = this.floatingAddButton.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.remove('hidden');
    }

    if (this.buyButtonBlock) {
      this.buyButtonBlock.disabled = true;
      const text = this.buyButtonBlock.querySelector('.bundle-builder__buy-button-text');
      if (text) text.textContent = 'Adding...';
      const spinner = this.buyButtonBlock.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.remove('hidden');
    }

    try {
      // Use boxId from API response
      const boxId = this.boxData?.boxId || this.boxProductId;

      // Build items array from Map, respecting quantities
      const items = [];
      this.selectedProducts.forEach(product => {
        // Extract numeric ID from GID format (e.g., "gid://shopify/ProductVariant/39601564909619" -> 39601564909619)
        let variantId = product.variantId;
        if (typeof variantId === 'string' && variantId.includes('gid://')) {
          variantId = variantId.split('/').pop();
        }

        const properties = {
          '_dynamicBoxId': boxId,
          '_dynamicBoxIndex': '0',
          '_bundleProductId': this.boxProductId,
          '_bundleItem': 'true'
        };

        // Add subscription info to properties for Skio to process
        if (this.isSubscription && this.selectedSellingPlan) {
          properties['_dynamicBoxSellingPlan'] = this.selectedSellingPlan;
        }

        const item = {
          id: parseInt(variantId, 10),
          quantity: product.quantity,
          properties
        };

        // Add selling plan for subscription
        if (this.isSubscription && this.selectedSellingPlan && this.boxData?.sellingPlanGroup) {
          const planGroup = this.boxData.sellingPlanGroup[this.selectedSellingPlan];
          let sellingPlanId = null;

          // Get the numeric variant ID for lookup
          let lookupId = product.variantId;
          if (typeof lookupId === 'string' && lookupId.includes('gid://')) {
            lookupId = lookupId.split('/').pop();
          }

          // Always check variantMapping first - even when allMatch is true,
          // each variant may have its own specific selling plan ID
          const hasVariantMapping = planGroup?.variantMapping && Object.keys(planGroup.variantMapping).length > 0;

          if (hasVariantMapping) {
            // Try both numeric and GID formats
            // variantMapping structure: { "gid://...": { planId: "gid://...", adjustedPrice: X } }
            const mapping = planGroup.variantMapping[lookupId] ||
                           planGroup.variantMapping[product.variantId] ||
                           planGroup.variantMapping[`gid://shopify/ProductVariant/${lookupId}`];

            // Extract planId if mapping is an object, otherwise use directly
            if (mapping) {
              sellingPlanId = typeof mapping === 'object' ? mapping.planId : mapping;
            }
          }

          if (sellingPlanId) {
            // Extract numeric ID from GID format if needed
            if (typeof sellingPlanId === 'string' && sellingPlanId.includes('gid://')) {
              sellingPlanId = sellingPlanId.split('/').pop();
            }
            item.selling_plan = parseInt(sellingPlanId, 10);
          }
        }

        items.push(item);
      });

      // Add free gift items if enabled (skip any already in cart)
      if (this.giftEnabled && this.giftVariantIds.length > 0) {
        const shouldAddGift = !this.giftSubscriptionOnly || this.isSubscription;

        if (shouldAddGift) {
          const existingGifts = await this.getExistingGiftVariantIds();
          this.giftVariantIds.forEach(giftVariantId => {
            if (existingGifts.has(String(giftVariantId))) return;
            const giftItem = {
              id: parseInt(giftVariantId, 10),
              quantity: 1,
              properties: {
                '_dynamicBoxId': boxId,
                '_dynamicBoxIndex': '0',
                '_bundleGift': 'true',
                '_bundleProductId': this.boxProductId,
                'Gift': 'Free'
              }
            };

            if (this.isSubscription && this.selectedSellingPlan) {
              giftItem.selling_plan = parseInt(this.selectedSellingPlan, 10);
            }

            items.push(giftItem);
          });
        }
      }

      // Add upsell items if any selected
      if (this.selectedUpsells.size > 0) {
        this.selectedUpsells.forEach(upsell => {
          let variantId = upsell.variantId;
          if (typeof variantId === 'string' && variantId.includes('gid://')) {
            variantId = variantId.split('/').pop();
          }

          // Note: Upsells do NOT include _dynamicBoxId to prevent Skio from applying tier discount
          const upsellItem = {
            id: parseInt(variantId, 10),
            quantity: upsell.quantity,
            properties: {
              '_bundleUpsell': 'true',
              '_bundleProductId': this.boxProductId
            }
          };

          items.push(upsellItem);
        });
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      const responseData = await response.json();

      if (!response.ok) {
        console.error('Cart error response:', response.status, response.statusText);
        console.error('Cart error data:', responseData);
        throw new Error(responseData.message || responseData.description || `Cart Error: ${response.status}`);
      }

      // Clear selections after successful add
      this.selectedProducts.clear();
      this.selectedUpsells.clear();

      // Reset to selection step
      this.currentStep = 'selection';
      this.container.classList.remove('bundle-builder--upsells-active');
      if (this.selectionStep) this.selectionStep.style.display = '';
      if (this.upsellsStep) this.upsellsStep.style.display = 'none';

      // Show other page content again
      const productInfoWrapper = this.container.querySelector('.product__info-wrapper');
      const mobileHeader = this.container.querySelector('.bundle-builder__mobile-header');
      if (productInfoWrapper) productInfoWrapper.style.display = '';
      if (mobileHeader) mobileHeader.style.display = '';
      if (this.floatingBar) this.floatingBar.style.display = '';

      // Clear floating upsell slots
      this.updateFloatingUpsells();

      // Open cart drawer (if available)
      this.openCartDrawer();

    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('There was an error adding items to your cart. Please try again.');
    } finally {
      // Always hide spinners and re-enable buttons
      if (this.floatingAddButton) {
        this.floatingAddButton.classList.remove('loading');
        const spinner = this.floatingAddButton.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.add('hidden');
      }
      if (this.buyButtonBlock) {
        this.buyButtonBlock.classList.remove('loading');
        const spinner = this.buyButtonBlock.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.add('hidden');
      }
      this.updateUI();
    }
  }

  showError(message) {
    if (this.grid) {
      this.grid.innerHTML = `<p class="bundle-builder__error">${this.escapeHtml(message)}</p>`;
    }
  }

  formatMoney(cents) {
    const amount = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.boxData?.currencyCode || 'USD'
    }).format(amount);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  initOkendoWidgets() {
    // Initialize Okendo widgets for dynamically added content
    // Uses the Widget+ Window API: https://docs.okendo.io/on-site/storefront-javascript-api/widget-plus-window-api
    const starRatingEl = this.drawerBody.querySelector('[data-oke-star-rating]');
    const carouselEl = this.drawerBody.querySelector('[data-oke-carousel]');

    const initWidgets = () => {
      if (window.okeWidgetApi && typeof window.okeWidgetApi.initWidget === 'function') {
        if (starRatingEl) window.okeWidgetApi.initWidget(starRatingEl);
        if (carouselEl) window.okeWidgetApi.initWidget(carouselEl);
        return true;
      }
      return false;
    };

    if (initWidgets()) return;

    // If Okendo API isn't loaded yet, wait and retry
    const checkOkendo = setInterval(() => {
      if (initWidgets()) clearInterval(checkOkendo);
    }, 100);
    // Stop checking after 3 seconds
    setTimeout(() => clearInterval(checkOkendo), 3000);
  }

  openCartDrawer() {
    window.suppressCartDrawer = false;

    // Try UpCart first (third-party cart drawer)
    if (typeof window.upcartOpenCart === 'function') {
      window.upcartOpenCart();
      return;
    }

    // Fallback to theme's cart drawer
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.open();
    }
  }

  closeCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.close();
    }
  }
}

// Initialize bundle builders
function initBundleBuilders(container = document) {
  const bundleBuilders = container.querySelectorAll('.bundle-builder:not([data-initialized])');
  bundleBuilders.forEach(el => {
    el.setAttribute('data-initialized', 'true');
    new BundleBuilder(el);
  });
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  initBundleBuilders();
});

// Re-initialize when section is loaded in theme customizer
document.addEventListener('shopify:section:load', (event) => {
  initBundleBuilders(event.target);
});
